import dayjs from 'dayjs';
import { Moves } from '@gql/base-types';

export interface EnrichedMove {
  move: Moves;

  // Planned times (from estimates)
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  plannedDurationMin: number;

  // Actual times (from status timestamps)
  actualStartTime: string | null;
  actualEndTime: string | null;
  actualDurationMin: number | null;

  // Delay segments (in minutes, for width calculation)
  pickupDelayMin: number;
  driveDelayMin: number;
  deliveryDelayMin: number;
  totalDelayMin: number;

  // Visual times (accounts for collision prevention)
  visualStartTime: string;
  visualEndTime: string;

  // Status flags
  isInProgress: boolean;
  isCompleted: boolean;
  isCanceled: boolean;
  currentPhase: 'pending' | 'pickup' | 'drive' | 'delivery' | 'completed';
}

// Delay caps in minutes
const PICKUP_DELAY_CAP_MIN = 60;
const DRIVE_DELAY_CAP_MIN = 120;
const DELIVERY_DELAY_CAP_MIN = 60;

/**
 * Build an enriched move with planned/actual times, delays, and visual positioning.
 * Handles collision prevention with the previous move.
 */
export const buildEnrichedMove = (
  move: Moves,
  prevEnrichedMove: EnrichedMove | null,
  isToday: boolean,
  currentTime: dayjs.Dayjs
): EnrichedMove => {
  // 1. Calculate planned times
  const plannedStartTime = move.ready_by || null;
  const plannedDurationSec = move.lane?.duration_sec || 0;
  const plannedDurationMin = plannedDurationSec / 60;
  const plannedEndTime = plannedStartTime
    ? dayjs(plannedStartTime).add(plannedDurationSec, 'second').toISOString()
    : null;

  // 2. Calculate actual times from status timestamps
  const actualStartTime = move.pickup_started || null;
  const actualEndTime = move.delivery_successful || null;
  const actualDurationMin =
    actualStartTime && actualEndTime
      ? dayjs(actualEndTime).diff(dayjs(actualStartTime), 'minute')
      : null;

  // 3. Determine current phase and status
  const status = move.status?.toLowerCase() || '';
  const isCompleted = status === 'delivery successful' || !!move.delivery_successful;
  const isCanceled = !!move.cancel_status;
  const isInProgress =
    !isCompleted &&
    !isCanceled &&
    (status.includes('pickup') || status.includes('delivery'));

  const currentPhase = getCurrentPhase(move);

  // 4. Calculate phase-specific delays
  const { pickupDelayMin, driveDelayMin, deliveryDelayMin } = calculateDelays(
    move,
    isToday,
    currentTime
  );

  const totalDelayMin = pickupDelayMin + driveDelayMin + deliveryDelayMin;

  // 5. Calculate visual start time (collision handling)
  let visualStartTime = plannedStartTime || currentTime.toISOString();

  // Collision prevention: if previous move's visual end is after this move's planned start, push out
  if (prevEnrichedMove?.visualEndTime) {
    const prevEnd = dayjs(prevEnrichedMove.visualEndTime);
    const thisStart = dayjs(visualStartTime);
    if (prevEnd.isAfter(thisStart)) {
      visualStartTime = prevEnrichedMove.visualEndTime;
    }
  }

  // For today, if move hasn't started and current time is past planned start, push out to current time
  if (isToday && !actualStartTime && currentTime.isAfter(dayjs(visualStartTime))) {
    visualStartTime = currentTime.toISOString();
  }

  // 6. Calculate visual end time (planned duration + all delays)
  const visualEndTime = dayjs(visualStartTime)
    .add(plannedDurationMin + totalDelayMin, 'minute')
    .toISOString();

  return {
    move,
    plannedStartTime,
    plannedEndTime,
    plannedDurationMin,
    actualStartTime,
    actualEndTime,
    actualDurationMin,
    pickupDelayMin,
    driveDelayMin,
    deliveryDelayMin,
    totalDelayMin,
    visualStartTime,
    visualEndTime,
    isInProgress,
    isCompleted,
    isCanceled,
    currentPhase,
  };
};

/**
 * Determine the current phase of the move based on status timestamps.
 */
const getCurrentPhase = (move: Moves): EnrichedMove['currentPhase'] => {
  if (move.delivery_successful) return 'completed';
  if (move.delivery_started || move.delivery_arrived) return 'delivery';
  if (move.pickup_successful) return 'drive';
  if (move.pickup_started || move.pickup_arrived) return 'pickup';
  return 'pending';
};

/**
 * Calculate phase-specific delays (pickup, drive, delivery).
 * For in-progress moves on today, includes real-time delay calculation.
 * Applies caps to prevent excessive visual stretching.
 */
const calculateDelays = (
  move: Moves,
  isToday: boolean,
  currentTime: dayjs.Dayjs
): { pickupDelayMin: number; driveDelayMin: number; deliveryDelayMin: number } => {
  let pickupDelayMin = 0;
  let driveDelayMin = 0;
  let deliveryDelayMin = 0;

  // Get planned durations for each phase
  const plannedPickupMin = (move.lane?.pickup_inspection_sec || 0) / 60;
  const plannedDriveMin = (move.lane?.duration_sec || 0) / 60;
  const plannedDeliveryMin = (move.lane?.delivery_inspection_sec || 0) / 60;

  // Calculate actual phase durations if timestamps exist
  if (move.pickup_started && move.pickup_successful) {
    const actualPickupMin = dayjs(move.pickup_successful).diff(
      dayjs(move.pickup_started),
      'minute'
    );
    pickupDelayMin = Math.max(0, actualPickupMin - plannedPickupMin);
  }

  if (move.pickup_successful && move.delivery_arrived) {
    const actualDriveMin = dayjs(move.delivery_arrived).diff(
      dayjs(move.pickup_successful),
      'minute'
    );
    driveDelayMin = Math.max(0, actualDriveMin - plannedDriveMin);
  }

  if (move.delivery_arrived && move.delivery_successful) {
    const actualDeliveryMin = dayjs(move.delivery_successful).diff(
      dayjs(move.delivery_arrived),
      'minute'
    );
    deliveryDelayMin = Math.max(0, actualDeliveryMin - plannedDeliveryMin);
  }

  // For in-progress moves on today, calculate live delay based on current time
  if (isToday && !move.delivery_successful) {
    const phase = getCurrentPhase(move);

    if (phase === 'pickup' && move.pickup_started) {
      const expectedEnd = dayjs(move.pickup_started).add(plannedPickupMin, 'minute');
      if (currentTime.isAfter(expectedEnd)) {
        pickupDelayMin = currentTime.diff(expectedEnd, 'minute');
      }
    }

    if (phase === 'drive' && move.pickup_successful) {
      const expectedEnd = dayjs(move.pickup_successful).add(plannedDriveMin, 'minute');
      if (currentTime.isAfter(expectedEnd)) {
        driveDelayMin = currentTime.diff(expectedEnd, 'minute');
      }
    }

    if (phase === 'delivery' && move.delivery_arrived) {
      const expectedEnd = dayjs(move.delivery_arrived).add(plannedDeliveryMin, 'minute');
      if (currentTime.isAfter(expectedEnd)) {
        deliveryDelayMin = currentTime.diff(expectedEnd, 'minute');
      }
    }
  }

  // Apply caps to prevent excessive visual stretching
  pickupDelayMin = Math.min(pickupDelayMin, PICKUP_DELAY_CAP_MIN);
  driveDelayMin = Math.min(driveDelayMin, DRIVE_DELAY_CAP_MIN);
  deliveryDelayMin = Math.min(deliveryDelayMin, DELIVERY_DELAY_CAP_MIN);

  return { pickupDelayMin, driveDelayMin, deliveryDelayMin };
};
